/**
 * TodayView — F 72 MVP phase 3 (default landing) + F 83 Stage 2 (5-status model).
 *
 * Renders three primary sections on the Today landing:
 *   - Inbox (status='inbox') — unprocessed tasks
 *   - Today (status='today' OR due=today OR overdue) — committed work
 *   - Done (status='done') — F 83 Stage 2 added this section per Gemini verdict
 *
 * Reads tasks via TaskStore. No external state management.
 * Designed to be embedded inside any surface that has a "main" slot.
 *
 * For per-view navigation across all 5 statuses (INBOX / TODAY / UPCOMING /
 * ANYTIME / DONE) with processing actions, use TasksView instead.
 */

import './TodayView.css';
import React, { useEffect, useState } from 'react';
import type { Task } from '../../../types/models/Task';
import { isOverdue, isDueToday, statusLabel, normalizeTaskStatus } from '../../../types/models/Task';
import { TaskStore } from '../../../core/task/store';

export interface TodayViewProps {
  store?: TaskStore;
}

interface Section<T> {
  label: string;
  icon: string;
  items: T[];
  empty: string;
}

export const TodayView: React.FC<TodayViewProps> = ({ store }) => {
  const internalStore = React.useMemo(() => store ?? new TaskStore(), [store]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Load + subscribe
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const all = await internalStore.list();
      if (!alive) return;
      setTasks(all);
      setLoaded(true);
    };
    void refresh();
    const unsub = internalStore.subscribe(() => { void refresh(); });
    return () => { alive = false; unsub(); };
  }, [internalStore]);

  const today = new Date();
  const todayDate = today.toISOString().slice(0, 10);

  // F 83 Stage 2: 'inbox' now uses normalized mapping (legacy 'open' → 'inbox')
  const inbox: Task[] = tasks
    .filter((t) => normalizeTaskStatus(t.status) === 'inbox')
    .sort((a, b) => priorityOrder(a) - priorityOrder(b) || a.createdAt.localeCompare(b.createdAt));

  // F 83 Stage 2: Today section includes explicit 'today' status + due=today/overdue.
  // The view stays the "Today landing surface" — broader than TasksView's today filter.
  const dueToday: Task[] = tasks
    .filter((t) => {
      const normalized = normalizeTaskStatus(t.status);
      if (normalized === 'done') return false;
      if (normalized === 'today') return true;
      return isDueToday(t, today) || isOverdue(t, today);
    })
    .sort((a, b) => {
      const overdueDelta = Number(isOverdue(b, today)) - Number(isOverdue(a, today));
      if (overdueDelta !== 0) return overdueDelta;
      return (a.due ?? '9999').localeCompare(b.due ?? '9999');
    });

  // F 83 Stage 2: Done section (per Gemini verdict — add a "Done" section).
  const done: Task[] = tasks
    .filter((t) => normalizeTaskStatus(t.status) === 'done')
    .sort((a, b) => (b.completedAt ?? b.updatedAt).localeCompare(a.completedAt ?? a.updatedAt));

  const sections: Section<Task>[] = [
    {
      label: 'Inbox',
      icon: '📥',
      items: inbox,
      empty: 'No new tasks. Press Cmd+T to capture one.',
    },
    {
      label: `Today (${todayDate})`,
      icon: '📅',
      items: dueToday,
      empty: 'Nothing due today. Enjoy the headspace.',
    },
    {
      label: 'Done',
      icon: '✓',
      items: done,
      empty: 'Nothing completed yet. Tick a task to mark it done.',
    },
  ];

  return (
    <div className="fc-today-view" data-testid="fc-today-view">
      <header className="fc-today-view__header">
        <h2>Today</h2>
        <span className="fc-today-view__date">{todayDate}</span>
        <span className="fc-today-view__counts">
          {dueToday.length} due · {inbox.length} in inbox · {done.length} done
        </span>
      </header>
      {!loaded && <p className="fc-today-view__loading">Loading…</p>}
      {loaded && sections.map((s) => (
        <section key={s.label} className="fc-today-view__section" data-section={s.label}>
          <h3>
            <span aria-hidden="true">{s.icon}</span> {s.label}
            <span className="fc-today-view__count"> ({s.items.length})</span>
          </h3>
          {s.items.length === 0 ? (
            <p className="fc-today-view__empty">{s.empty}</p>
          ) : (
            <ul className="fc-today-view__list">
              {s.items.map((t) => (
                <li
                  key={t.id}
                  className={
                    'fc-today-view__item' +
                    (normalizeTaskStatus(t.status) === 'done' ? ' fc-today-view__item--done' : '')
                  }
                  data-task-id={t.id}
                  data-task-status={t.status}
                >
                  <span className="fc-today-view__status">{statusLabel(t.status)}</span>
                  <span className="fc-today-view__title">{t.title}</span>
                  {t.due && (
                    <span
                      className={
                        'fc-today-view__due' +
                        (isOverdue(t, today) ? ' fc-today-view__due--overdue' : '')
                      }
                    >
                      {t.due}
                    </span>
                  )}
                  <span className="fc-today-view__priority" data-priority={t.priority}>
                    P{t.priority}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
};

function priorityOrder(t: Task): number {
  // p1 first (lower number = higher priority)
  return t.priority;
}

export default TodayView;
